import { findGeoLocation } from '@/api/geoService';
import { getPriceEvolution, getPriceRoomDistribution } from '@/api/iaziApi';
import { API, fetchPropertyData, updatePropertyById } from '@/api/propertyValuation';
import { PriceEvolutionResponse } from '@/models/queries/getPriceEvolution';
import { PriceRoomDistributionResponse } from '@/models/queries/getPriceRoomDistribution';
import { PropertyValuationResult } from '@/models/queries/getPropertyData';
import { HomeOwnerWorldPropertyUpdateRequest } from '@/store/modules/propertyValuation/types';
import { AppState } from '@/store/types';
import { ToastType } from '@/types';
import { Intent } from '@/types/intent';
import { captureAction } from '@smg-real-estate/browser-rum';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { GeoLocation } from '@smg-real-estate/geo-libs';
import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { ListingOfferings, retrieveFilteredListings, sortListingsByDistance } from './utils';

export interface PropertyDetailsModule extends Module<PropertyDetailsState, AppState> {
  state: () => PropertyDetailsState;
  getters: PropertyDetailsGetters;
  mutations: PropertyDetailsMutations;
  actions: PropertyDetailsActions;
}

interface PriceData {
  priceTrends: PriceEvolutionResponse;
  priceByRoom: PriceRoomDistributionResponse;
}

const defaultPriceData: PriceData = {
  priceTrends: null,
  priceByRoom: null,
};

export interface PropertyDetailsState {
  isPropertyDataLoading: boolean;
  propertyData: PropertyValuationResult | null;
  hasResolveErrors: boolean;
  isPriceDataLoading: boolean;
  priceData: PriceData;
  geoLocation: GeoLocation | null;
  toastType: ToastType;
  similarListings: ListingOfferings | null;
  totalBuyListings: number;
  totalRentListings: number;
  wasNotEngaged: boolean;
}

interface PropertyDetailsGetters extends GetterTree<PropertyDetailsState, AppState> {
  getIsPropertyDataLoading: (state: PropertyDetailsState) => boolean;
  getPropertyData: (state: PropertyDetailsState) => PropertyValuationResult | null;
  getHasResolveErrors: (state: PropertyDetailsState) => boolean;
  getIsPriceDataLoading: (state: PropertyDetailsState) => boolean;
  getPriceData: (state: PropertyDetailsState) => PriceData;
  getGeoLocation: (state: PropertyDetailsState) => GeoLocation | null;
  getToastType: (state: PropertyDetailsState) => ToastType;
  getSimilarListings: (state: PropertyDetailsState) => ListingOfferings | null;
  getTotalBuyListings: (state: PropertyDetailsState) => number;
  getTotalRentListings: (state: PropertyDetailsState) => number;
  getWasNotEngaged: (state: PropertyDetailsState) => boolean;
}

interface PropertyDetailsMutations extends MutationTree<PropertyDetailsState> {
  setIsPropertyDataLoading: (state: PropertyDetailsState, isLoading: boolean) => void;
  setPropertyData: (
    state: PropertyDetailsState,
    propertyData: PropertyValuationResult | null,
  ) => void;
  setHasResolveErrors: (state: PropertyDetailsState, hasResolveErrors: boolean) => void;
  setIsPriceDataLoading: (state: PropertyDetailsState, isLoading: boolean) => void;
  setPriceData: (state: PropertyDetailsState, priceData: PriceData) => void;
  setGeoLocation: (state: PropertyDetailsState, geoLocation: GeoLocation | null) => void;
  setToastType: (state: PropertyDetailsState, type: ToastType) => void;
  setSimilarListings: (state: PropertyDetailsState, listings: ListingOfferings | null) => void;
  setListingTotals: (state: PropertyDetailsState, totals: { buy: number; rent: number }) => void;
  setWasNotEngaged: (state: PropertyDetailsState, value: boolean) => void;
}

interface PropertyDetailsActions extends ActionTree<PropertyDetailsState, AppState> {
  fetchPropertyData: (
    actionContext: ActionContext<PropertyDetailsState, AppState>,
    payload: { leadId: string; retries: number },
  ) => Promise<void>;
  updatePropertyIntent: (
    actionContext: ActionContext<PropertyDetailsState, AppState>,
    payload: { intent: Intent },
  ) => Promise<boolean>;
  dismissRenovationDisclaimer: (
    actionContext: ActionContext<PropertyDetailsState, AppState>,
  ) => Promise<boolean>;
  updateRenovationDetails: (
    actionContext: ActionContext<PropertyDetailsState, AppState>,
    payload: HomeOwnerWorldPropertyUpdateRequest,
  ) => Promise<boolean>;
  fetchPropertyPricesData: (
    actionContext: ActionContext<PropertyDetailsState, AppState>,
  ) => Promise<void>;
  resetPriceData: (actionContext: ActionContext<PropertyDetailsState, AppState>) => void;
  fetchSimilarListings: (
    actionContext: ActionContext<PropertyDetailsState, AppState>,
  ) => Promise<void>;
}

const state = (): PropertyDetailsState => getDefaultState();
const getters: PropertyDetailsGetters = {
  getIsPropertyDataLoading: (state) => state.isPropertyDataLoading,
  getPropertyData: (state) => state.propertyData,
  getHasResolveErrors: (state) => state.hasResolveErrors,
  getIsPriceDataLoading: (state) => state.isPriceDataLoading,
  getPriceData: (state) => state.priceData,
  getGeoLocation: (state) => state.geoLocation,
  getToastType: (state) => state.toastType,
  getSimilarListings: (state) => state.similarListings,
  getTotalBuyListings: (state) => state.totalBuyListings,
  getTotalRentListings: (state) => state.totalRentListings,
  getWasNotEngaged: (state) => state.wasNotEngaged,
};

const mutations: PropertyDetailsMutations = {
  setIsPropertyDataLoading: (state, isLoading) => (state.isPropertyDataLoading = isLoading),
  setPropertyData: (state, propertyData) => (state.propertyData = propertyData),
  setHasResolveErrors: (state, hasResolveErrors) => (state.hasResolveErrors = hasResolveErrors),
  setIsPriceDataLoading: (state, isLoading) => (state.isPriceDataLoading = isLoading),
  setPriceData: (state, priceData) => (state.priceData = priceData),
  setGeoLocation: (state, geoLocation) => (state.geoLocation = geoLocation),
  setToastType: (state, type) => (state.toastType = type),
  setSimilarListings: (state, listings) => (state.similarListings = listings),
  setListingTotals: (state, totals) => {
    state.totalBuyListings = totals.buy;
    state.totalRentListings = totals.rent;
  },
  setWasNotEngaged: (state, value) => (state.wasNotEngaged = value),
};

const actions: PropertyDetailsActions = {
  fetchPropertyData: async (
    { commit, dispatch, getters }: ActionContext<PropertyDetailsState, AppState>,
    payload,
  ) => {
    let propertyData = getters.getPropertyData;
    if (propertyData?.leadId !== payload.leadId) {
      commit('setIsPropertyDataLoading', true);
      commit('setHasResolveErrors', false);
      try {
        propertyData = await fetchPropertyData(payload.leadId, payload.retries, API.HomeOwnerWorld);
      } catch {
        propertyData = null;
      }
    }

    if (propertyData) {
      if (!propertyData.intent) {
        propertyData = { ...propertyData, intent: Intent.Inform };
      }

      // Check hasNotEngaged before any update can toggle it on the backend.
      // If already set via the overview click handler, preserve it.
      if (propertyData.hasNotEngaged && !getters.getWasNotEngaged) {
        commit('setWasNotEngaged', true);
      }

      const searchText = propertyData.zipCode ?? propertyData.town ?? '';
      // should always be EN as the database location data(google map) is in English
      const geoLocation = await findGeoLocation(searchText, HgSupportedLanguage.EN);
      commit('setGeoLocation', geoLocation);
      dispatch('fetchPropertyPricesData');
    } else {
      commit('setHasResolveErrors', true);
    }
    commit('setPropertyData', propertyData);
    commit('setIsPropertyDataLoading', false);
  },

  updatePropertyIntent: async (
    { commit, getters }: ActionContext<PropertyDetailsState, AppState>,
    payload: { intent: Intent },
  ): Promise<boolean> => {
    const property = getters.getPropertyData;
    const intent = payload?.intent;
    if (!property?.leadId || !intent) return false;
    const updatePayload = { intent: String(intent) };
    const updated = await updatePropertyById(property.leadId, updatePayload);
    if (updated) {
      // Merge to ensure intent is in store even if API returns partial response
      commit('setPropertyData', { ...property, ...updated, intent });
      return true;
    }
    return false;
  },

  dismissRenovationDisclaimer: async ({
    commit,
    getters,
  }: ActionContext<PropertyDetailsState, AppState>): Promise<boolean> => {
    const property = getters.getPropertyData;
    if (!property?.leadId) return false;
    const { leadId } = property;

    commit('setPropertyData', {
      ...property,
      renovationDataDisclaimerSeen: true,
    });

    const updated = await updatePropertyById(leadId, {
      renovationDataDisclaimerSeen: true,
    });

    // The user can switch properties while the request is in flight; never write
    // this property's response over whichever property is displayed now.
    if (getters.getPropertyData?.leadId !== leadId) return false;

    if (!updated) {
      // Dismissing is not critical: keep the overlay closed and only report the failure.
      captureAction('Failed to persist renovation disclaimer dismissal', { leadId });
      return false;
    }

    commit('setPropertyData', {
      ...getters.getPropertyData,
      ...updated,
      renovationDataDisclaimerSeen: true,
    });
    return true;
  },

  updateRenovationDetails: async (
    { commit, getters }: ActionContext<PropertyDetailsState, AppState>,
    payload: HomeOwnerWorldPropertyUpdateRequest,
  ): Promise<boolean> => {
    const property = getters.getPropertyData;
    if (!property?.leadId) return false;
    const updated = await updatePropertyById(property.leadId, payload);
    if (updated) {
      commit('setPropertyData', {
        ...property,
        ...updated,
        renovationDataProvidedByUser:
          updated.renovationDataProvidedByUser ??
          payload.renovationDataProvidedByUser ??
          property.renovationDataProvidedByUser,
      });
      return true;
    }
    return false;
  },

  fetchPropertyPricesData: async (
    context: ActionContext<PropertyDetailsState, AppState>,
  ): Promise<void> => {
    context.commit('setIsPriceDataLoading', true);
    const geoLocation = context.getters.getGeoLocation;
    if (!geoLocation) {
      await context.dispatch('resetPriceData');
      return;
    }

    context.commit('setIsPriceDataLoading', true);

    const level = geoLocation.iazi?.level;
    const levelId = geoLocation.iazi?.id;
    if (!level || !levelId) {
      captureAction('No iazi level and id found', {
        geoId: geoLocation.id,
      });
      await context.dispatch('resetPriceData');
      return;
    }

    const [priceTrends, priceByRoom] = await Promise.all([
      getPriceEvolution({ level, levelId }),
      getPriceRoomDistribution({ level, levelId }),
    ]);
    context.commit('setPriceData', {
      priceTrends,
      priceByRoom,
    });
    context.commit('setIsPriceDataLoading', false);
  },

  resetPriceData: ({ commit }: ActionContext<PropertyDetailsState, AppState>): void => {
    commit('setPriceData', defaultPriceData);
    commit('setGeoLocation', null);
    commit('setIsPriceDataLoading', false);
  },

  fetchSimilarListings: async (context: ActionContext<PropertyDetailsState, AppState>) => {
    const property = context.getters.getPropertyData;
    if (!property) {
      context.commit('setSimilarListings', []);
      context.commit('setListingTotals', { buy: 0, rent: 0 });
      return;
    }

    const similarListings = await retrieveFilteredListings(property);

    const center = context.state.geoLocation?.center;
    const geoCoordinates = { latitude: center?.lat || 0, longitude: center?.lon || 0 };

    context.commit('setListingTotals', {
      buy: similarListings.buy?.length || 0,
      rent: similarListings.rent?.length || 0,
    });

    const sortedLimitedListings = {
      buy: (center
        ? sortListingsByDistance(geoCoordinates, similarListings.buy)
        : similarListings.buy
      )?.slice(0, 50),
      rent: (center
        ? sortListingsByDistance(geoCoordinates, similarListings.rent)
        : similarListings.rent
      )?.slice(0, 50),
    };

    context.commit('setSimilarListings', sortedLimitedListings);
  },
};

export const getDefaultState = (): PropertyDetailsState => ({
  isPropertyDataLoading: false,
  propertyData: null,
  hasResolveErrors: false,
  isPriceDataLoading: false,
  priceData: defaultPriceData,
  geoLocation: null,
  toastType: ToastType.None,
  similarListings: null,
  totalBuyListings: 0,
  totalRentListings: 0,
  wasNotEngaged: false,
});

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
} as PropertyDetailsModule;
